import React, { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { formatMobileDisplay, parseMobileInput, isValidMobileNumber } from "../../../utils/mobileNumberUtils"
import { INDIAN_STATES, PARTNER_CITIES, getStateForCity } from "../../../utils/partnerOnboardingConfig"
import { submitPartnerLead, sendPartnerOtp, verifyPartnerOtp } from "../../../store/Partner/partnerApi"
import heroImg from "../../../assets/partner/partner-hero.webp"
import "./heroPartner.scss"

const TYPING_PHRASES = ["PAN India", "450+ Cities", "Outstation Routes", "Local Trips", "Fleet Partners"]
const VEHICLE_TYPES = ["Hatchback", "Sedan", "SUV", "Innova / MUV"]

const HeroPartner = () => {
  const navigate = useNavigate()
  const [partnerType, setPartnerType] = useState("individual")
  const [stage, setStage] = useState("form") // form | otp | success
  const [loading, setLoading] = useState(false)

  // OTP state
  const [otp, setOtp] = useState("")
  const [otpRequestId, setOtpRequestId] = useState("")
  const [partnerId, setPartnerId] = useState("")

  const [displayText, setDisplayText] = useState("")
  const phraseIdx = useRef(0)
  const charIdx = useRef(0)
  const isDeleting = useRef(false)

  const tick = useCallback(() => {
    const current = TYPING_PHRASES[phraseIdx.current]
    if (!isDeleting.current) {
      charIdx.current++
      setDisplayText(current.slice(0, charIdx.current))
      if (charIdx.current === current.length) { isDeleting.current = true; return 1800 }
      return 100
    } else {
      charIdx.current--
      setDisplayText(current.slice(0, charIdx.current))
      if (charIdx.current === 0) {
        isDeleting.current = false
        phraseIdx.current = (phraseIdx.current + 1) % TYPING_PHRASES.length
        return 400
      }
      return 50
    }
  }, [])

  useEffect(() => {
    let id
    const run = () => { const delay = tick(); id = setTimeout(run, delay) }
    run()
    return () => clearTimeout(id)
  }, [tick])

  const [formData, setFormData] = useState({
    fullName: "", mobile: "", email: "", city: "", state: "",
    vehicleType: "", companyName: "", numberOfVehicles: "", vehicleTypes: [],
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === "mobile") {
      setFormData((prev) => ({ ...prev, mobile: parseMobileInput(value) }))
    } else if (name === "city") {
      // auto-fill state from city, but let the vendor override it after
      setFormData((prev) => ({ ...prev, city: value, state: getStateForCity(value) || prev.state }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleVehicleTypeToggle = (type) => {
    setFormData((prev) => {
      const types = prev.vehicleTypes.includes(type)
        ? prev.vehicleTypes.filter((t) => t !== type)
        : [...prev.vehicleTypes, type]
      return { ...prev, vehicleTypes: types }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.fullName.trim()) return toast.error("Please enter your full name")
    if (!isValidMobileNumber(formData.mobile)) return toast.error("Enter a valid 10-digit mobile number")
    if (!formData.email.trim() || !formData.email.includes("@")) return toast.error("Enter a valid email")
    if (!formData.city) return toast.error("Please select a city")
    if (!formData.state) return toast.error("Please select a state")
    if (partnerType === "individual" && !formData.vehicleType) return toast.error("Please select a vehicle type")
    if (partnerType === "fleet" && !formData.companyName.trim()) return toast.error("Please enter your company name")
    if (partnerType === "fleet" && (!formData.numberOfVehicles || Number(formData.numberOfVehicles) < 1)) return toast.error("Please enter number of vehicles")

    const submitData = {
      name: formData.fullName,
      mobile_number: formData.mobile,
      email: formData.email,
      city: formData.city,
      state: formData.state,
      partner_type: partnerType,
      ...(partnerType === "individual"
        ? { vehicle_types: formData.vehicleType }
        : {
            company_name: formData.companyName,
            number_of_vehicles: formData.numberOfVehicles,
            vehicle_types: formData.vehicleTypes.join(", "),
          }),
    }

    setLoading(true)
    // 1) Capture the lead (this endpoint exists today).
    const lead = await submitPartnerLead(submitData)
    if (!lead.ok && !lead.notImplemented) {
      setLoading(false)
      return toast.error(lead.message || "Something went wrong. Please try again.")
    }
    if (lead.data?.partner_id) setPartnerId(lead.data.partner_id)

    // 2) Try to verify the mobile by OTP. If that endpoint is not live yet,
    //    fall through to success so the vendor is never blocked.
    const sent = await sendPartnerOtp(formData.mobile)
    setLoading(false)
    if (sent.ok) {
      setOtpRequestId(sent.data?.request_id || "")
      setStage("otp")
      toast.success("OTP sent to your mobile")
    } else {
      setStage("success")
    }
  }

  const handleVerify = async () => {
    if (!/^\d{4,6}$/.test(otp)) return toast.error("Enter the OTP you received")
    setLoading(true)
    const res = await verifyPartnerOtp({ mobile_number: formData.mobile, otp, request_id: otpRequestId })
    setLoading(false)
    if (res.ok) {
      if (res.data?.partner_id) setPartnerId(res.data.partner_id)
      setStage("success")
    } else if (res.notImplemented) {
      setStage("success")
    } else {
      toast.error(res.message || "Incorrect OTP, please try again")
    }
  }

  const resendOtp = async () => {
    const sent = await sendPartnerOtp(formData.mobile)
    if (sent.ok) { setOtpRequestId(sent.data?.request_id || ""); toast.success("OTP resent") }
    else toast.info("Could not resend right now")
  }

  const goToFullForm = () => {
    const q = new URLSearchParams({
      m: formData.mobile,
      name: formData.fullName,
      email: formData.email,
      city: formData.city,
      state: formData.state,
      type: partnerType,
    })
    if (partnerId) q.set("pid", partnerId)
    navigate(`/cab-driver-registration/onboarding?${q.toString()}`)
  }

  const resetForm = () => {
    setStage("form"); setOtp(""); setOtpRequestId("")
    setFormData({ fullName: "", mobile: "", email: "", city: "", state: "", vehicleType: "", companyName: "", numberOfVehicles: "", vehicleTypes: [] })
  }

  return (
    <section className="hero-partner" style={{ backgroundImage: `url(${heroImg})` }}>
      <div className="hero-partner__overlay" />
      <div className="hero-partner__container">
        <div className="hero-partner__left">
          <h1 className="hero-partner__title">
            Drive &amp; Earn with <span>Pulpit</span>
            <br />
            <span className="hero-partner__typing">{displayText}<span className="hero-partner__cursor">|</span></span>
          </h1>
          <p className="hero-partner__subtitle">
            Attach your car or fleet for outstation and local cab bookings.
            Join India's fastest-growing cab platform - individual drivers
            and fleet owners welcome across 450+ cities.
          </p>
          <div className="hero-partner__stats">
            <div className="hero-partner__stat"><span className="hero-partner__stat-number">10K+</span><span className="hero-partner__stat-label">Rides Daily</span></div>
            <div className="hero-partner__stat"><span className="hero-partner__stat-number">450+</span><span className="hero-partner__stat-label">Cities</span></div>
            <div className="hero-partner__stat"><span className="hero-partner__stat-number">4.9/5</span><span className="hero-partner__stat-label">Rating</span></div>
          </div>
        </div>

        <div className="hero-partner__right">
          {stage === "success" ? (
            <div className="hero-partner__form-card hero-partner__form-card--success">
              <div className="hero-partner__success-icon">&#10003;</div>
              <h3 className="hero-partner__success-title">You're registered!</h3>
              <p className="hero-partner__success-text">
                Welcome aboard{formData.fullName ? `, ${formData.fullName.split(" ")[0]}` : ""}. To go live and start
                receiving trips, complete your full profile: fleet, rates, documents and bank details.
              </p>
              <button className="hero-partner__submit" onClick={goToFullForm}>
                Complete full registration &rarr;
              </button>
              <button className="hero-partner__success-btn" onClick={resetForm}>
                Register another partner
              </button>
            </div>
          ) : stage === "otp" ? (
            <div className="hero-partner__form-card">
              <h2 className="hero-partner__form-title">Verify your mobile</h2>
              <p className="hero-partner__form-subtitle">
                Enter the code sent to +91 {formatMobileDisplay(formData.mobile)}
              </p>
              <input
                type="tel"
                inputMode="numeric"
                className="hero-partner__input hero-partner__otp"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                autoFocus
              />
              <button type="button" className="hero-partner__submit" onClick={handleVerify} disabled={loading}>
                {loading ? "Verifying..." : "Verify & continue"}
              </button>
              <div className="hero-partner__otp-row">
                <button type="button" className="hero-partner__link" onClick={resendOtp}>Resend OTP</button>
                <button type="button" className="hero-partner__link" onClick={() => setStage("form")}>Change number</button>
              </div>
            </div>
          ) : (
            <div className="hero-partner__form-card">
              <h2 className="hero-partner__form-title">Register as Cab Driver</h2>
              <p className="hero-partner__form-subtitle">Free registration - start earning in 24 hours</p>

              <div className="hero-partner__toggle">
                <button type="button" className={`hero-partner__toggle-btn${partnerType === "individual" ? " hero-partner__toggle-btn--active" : ""}`} onClick={() => setPartnerType("individual")}>Individual Driver</button>
                <button type="button" className={`hero-partner__toggle-btn${partnerType === "fleet" ? " hero-partner__toggle-btn--active" : ""}`} onClick={() => setPartnerType("fleet")}>Fleet Owner</button>
              </div>

              <form onSubmit={handleSubmit} className="hero-partner__form">
                <input type="text" name="fullName" className="hero-partner__input" placeholder="Full Name *" value={formData.fullName} onChange={handleChange} required />

                <input
                  type="tel" name="mobile"
                  className={`hero-partner__input${isValidMobileNumber(formData.mobile) ? " hero-partner__input--valid" : formData.mobile.length > 0 ? " hero-partner__input--invalid" : ""}`}
                  placeholder="Mobile Number *" value={formatMobileDisplay(formData.mobile)} onChange={handleChange} maxLength={11} required
                />

                <input type="email" name="email" className="hero-partner__input" placeholder="Email Address *" value={formData.email} onChange={handleChange} required />

                {partnerType === "fleet" && (
                  <input type="text" name="companyName" className="hero-partner__input" placeholder="Company / Agency Name *" value={formData.companyName} onChange={handleChange} required />
                )}

                <div className="hero-partner__row2">
                  <select name="city" className={`hero-partner__input hero-partner__select${!formData.city ? " hero-partner__select--placeholder" : ""}`} value={formData.city} onChange={handleChange} required>
                    <option value="" disabled>Select City *</option>
                    {PARTNER_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select name="state" className={`hero-partner__input hero-partner__select${!formData.state ? " hero-partner__select--placeholder" : ""}`} value={formData.state} onChange={handleChange} required>
                    <option value="" disabled>Select State *</option>
                    {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                {partnerType === "individual" ? (
                  <select name="vehicleType" className={`hero-partner__input hero-partner__select${!formData.vehicleType ? " hero-partner__select--placeholder" : ""}`} value={formData.vehicleType} onChange={handleChange} required>
                    <option value="" disabled>Vehicle Type *</option>
                    {VEHICLE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                ) : (
                  <>
                    <input type="number" name="numberOfVehicles" className="hero-partner__input" placeholder="Number of Vehicles *" value={formData.numberOfVehicles} onChange={handleChange} min="1" required />
                    <div className="hero-partner__checkbox-group">
                      <span className="hero-partner__checkbox-label">Vehicle Types:</span>
                      <div className="hero-partner__checkboxes">
                        {VEHICLE_TYPES.map((v) => (
                          <label key={v} className="hero-partner__checkbox">
                            <input type="checkbox" checked={formData.vehicleTypes.includes(v)} onChange={() => handleVehicleTypeToggle(v)} />
                            <span>{v}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <button type="submit" className="hero-partner__submit" disabled={loading}>
                  {loading ? "Submitting..." : "Register as Partner"}
                </button>

                <p className="hero-partner__form-footer">By registering, you agree to our terms &amp; privacy policy.</p>
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default HeroPartner
